import { utcIsoToDateInput, utcIsoToTimeInput } from "./tz";

/**
 * Calendar — pure geometry for the attendee availability strip. Kept out of the
 * component so the day-boundary and DST behaviour is unit-testable without
 * rendering anything.
 *
 * The strip shows one horizontal track per person for a single organizer-local
 * calendar day, with busy time shaded. Positioning is done in WALL-CLOCK
 * MINUTES, never in elapsed UTC milliseconds: a local day is 23h or 25h long
 * across a DST transition, so a linear map over UTC would drift the shading
 * against the hour labels on those days. Reading each instant's local `HH:mm`
 * and placing it against the wall-clock window is exact every day of the year.
 */

/** Displayed window, organizer-local. No business-hours setting exists in the
 *  platform to derive this from; these are the strip's own defaults and are the
 *  only place to change it. */
export const DISPLAY_START_HOUR = 7; // 7 AM
export const DISPLAY_END_HOUR = 19; // 7 PM

const MINUTES_PER_DAY = 24 * 60;
const WINDOW_START_MIN = DISPLAY_START_HOUR * 60;
const WINDOW_END_MIN = DISPLAY_END_HOUR * 60;
const WINDOW_SPAN_MIN = WINDOW_END_MIN - WINDOW_START_MIN;

/**
 * A sliver of busy time would round to an invisible width, which reads as
 * "free". Floor the rendered width so a short meeting is still visible; the
 * tooltip carries the exact range.
 */
const MIN_BLOCK_WIDTH_PCT = 1.5;

/** `YYYY-MM-DD` one day after `dayIso`. Handles month/year/leap rollover. */
export function nextDayIso(dayIso: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dayIso)) return dayIso;
  const [y, m, d] = dayIso.split("-").map(Number);
  const next = new Date(Date.UTC(y, m - 1, d + 1));
  const yy = String(next.getUTCFullYear()).padStart(4, "0");
  const mm = String(next.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(next.getUTCDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}

/**
 * Minutes since local midnight for `iso` as observed on `dayIso` in `tz`,
 * clamped to the day: an instant on an earlier local date reads 0, one on a
 * later local date reads 1440. That clamping is what makes a block spanning a
 * day boundary render correctly instead of wrapping — a meeting that started at
 * 23:00 yesterday must not be placed at "minute 1380" of today.
 */
export function localMinutesOnDay(
  iso: string,
  dayIso: string,
  tz?: string | null,
): number {
  const localDate = utcIsoToDateInput(iso, tz);
  if (!localDate) return 0;
  if (localDate < dayIso) return 0;
  if (localDate > dayIso) return MINUTES_PER_DAY;

  const time = utcIsoToTimeInput(iso, tz);
  if (!time) return 0;
  const [hh, mm] = time.split(":").map(Number);
  if (Number.isNaN(hh) || Number.isNaN(mm)) return 0;
  return hh * 60 + mm;
}

export interface BlockPlacement {
  /** Distance from the track's left edge, as a percentage of its width. */
  leftPct: number;
  /** Width as a percentage of the track. */
  widthPct: number;
}

/**
 * Place one busy block on the track, or return `null` when it falls entirely
 * outside the displayed window (someone busy 6:00–6:45 AM simply isn't drawn —
 * correct for a 7–7 strip).
 *
 * An all-day block deliberately spans the whole track regardless of its stored
 * instants, so it reads as an intentional "all day" band rather than a
 * 00:00–23:59 shading artifact.
 */
export function placeBusyBlock(
  block: { startUtc: string; endUtc: string; isAllDay: boolean },
  dayIso: string,
  tz?: string | null,
): BlockPlacement | null {
  if (block.isAllDay) return { leftPct: 0, widthPct: 100 };

  const startMin = localMinutesOnDay(block.startUtc, dayIso, tz);
  const endMin = localMinutesOnDay(block.endUtc, dayIso, tz);

  const clampedStart = Math.max(startMin, WINDOW_START_MIN);
  const clampedEnd = Math.min(endMin, WINDOW_END_MIN);
  if (clampedEnd <= clampedStart) return null;

  const leftPct = ((clampedStart - WINDOW_START_MIN) / WINDOW_SPAN_MIN) * 100;
  const rawWidthPct = ((clampedEnd - clampedStart) / WINDOW_SPAN_MIN) * 100;
  const widthPct = Math.max(rawWidthPct, MIN_BLOCK_WIDTH_PCT);

  return {
    leftPct,
    // Keep a floored sliver inside the track rather than overflowing its right
    // edge.
    widthPct: Math.min(widthPct, 100 - leftPct),
  };
}

export interface HourTick {
  hour: number;
  label: string;
  leftPct: number;
}

/** Compact 12h label for an axis tick ("7a", "12p", "1p"). */
export function formatHourTick(hour: number): string {
  const suffix = hour < 12 ? "a" : "p";
  const h = hour % 12 === 0 ? 12 : hour % 12;
  return `${h}${suffix}`;
}

/**
 * Axis ticks across the displayed window. Every 2 hours by default — the modal
 * is narrow enough that hourly labels collide.
 */
export function hourTicks(stepHours = 2): HourTick[] {
  const ticks: HourTick[] = [];
  for (let hour = DISPLAY_START_HOUR; hour <= DISPLAY_END_HOUR; hour += stepHours) {
    ticks.push({
      hour,
      label: formatHourTick(hour),
      leftPct: ((hour * 60 - WINDOW_START_MIN) / WINDOW_SPAN_MIN) * 100,
    });
  }
  return ticks;
}
